import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);
  // Phase 3 (frontend gating): additive /auth/me data. restrictions_active is true only when the cafe runs enforcement ON.
  const [entitlements, setEntitlements] = useState(null);
  const [effectiveModules, setEffectiveModules] = useState({});

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setEntitlements(data.entitlements || null);
      setEffectiveModules(data.effective_modules || {});
    } catch {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  // Public customer routes never use staff auth — skip the /auth/me round trip there.
  const isPublicRoute = /^\/(order|table-order)\//.test(window.location.pathname);
  useEffect(() => { if (isPublicRoute) { setUser(false); setReady(true); } else refresh(); }, [refresh, isPublicRoute]);
  const authed = !!user;
  useEffect(() => {
    if (!authed) return undefined;
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    const t = setInterval(refresh, 60000);
    window.addEventListener("focus", onVisible); document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(t); window.removeEventListener("focus", onVisible); document.removeEventListener("visibilitychange", onVisible); };
  }, [authed, refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      refresh(); // entitlements / effective modules arrive with /auth/me
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setEntitlements(null);
    setEffectiveModules({});
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, refresh, entitlements, effectiveModules }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
